import {
    BIRD_POINTS,
    BirdType,
    getComboMilestone,
    getSpawnDifficulty,
    HUNT_ASSETS,
    HUNT_BACKEND_MODE,
    HUNT_DURATION_MS,
    HUNT_EDGE_Y_MAX,
    HUNT_EDGE_Y_MIN,
    HUNT_SPAWN_LANES,
} from './huntConfig';

export type BirdState = 'fly' | 'fall' | 'dead';

export interface HuntBird {
    id: number;
    type: BirdType;
    x: number;
    y: number;
    vx: number;
    vy: number;
    width: number;
    height: number;
    direction: 1 | -1;
    frame: number;
    frameTimer: number;
    state: BirdState;
    rotation: number;
    hitAt?: number;
}

export interface HuntHits {
    normal: number;
    fast: number;
    bonus: number;
}

export interface HuntGameSnapshot {
    birds: HuntBird[];
    score: number;
    comboStreak: number;
    comboBonus: number;
    hits: HuntHits;
    timeLeftMs: number;
    pointerX: number;
    pointerY: number;
    flashUntil: number;
    bonusSpawned: boolean;
    bonusMissed: number;
    finished: boolean;
}

export interface HuntHitResult {
    points: number;
    comboBonus: number;
    comboStreak: number;
    comboMilestone: number | null;
}

export interface HuntGameResult {
    score: number;
    hits: HuntHits;
    comboBonus: number;
    durationMs: number;
    mode: typeof HUNT_BACKEND_MODE;
    bonusMissed: number;
}

let nextBirdId = 1;

export function resetBirdIds(): void {
    nextBirdId = 1;
}

export function createInitialSnapshot(): HuntGameSnapshot {
    return {
        birds: [],
        score: 0,
        comboStreak: 0,
        comboBonus: 0,
        hits: { normal: 0, fast: 0, bonus: 0 },
        timeLeftMs: HUNT_DURATION_MS,
        pointerX: -100,
        pointerY: -100,
        flashUntil: 0,
        bonusSpawned: false,
        bonusMissed: 0,
        finished: false,
    };
}

function randomBetween(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function pickBirdType(snapshot: HuntGameSnapshot, elapsedMs: number): BirdType {
    const diff = getSpawnDifficulty(elapsedMs);
    if (!snapshot.bonusSpawned && elapsedMs > 10000 && Math.random() < 0.16) {
        return 'bonus';
    }
    if (Math.random() < diff.fastChance) {
        return 'fast';
    }
    return 'normal';
}

function birdDimensions(type: BirdType): { width: number; height: number } {
    const def = HUNT_ASSETS[type];
    return {
        width: def.frameWidth * def.scale,
        height: def.frameHeight * def.scale,
    };
}

function spawnY(canvasHeight: number, birdHeight: number): number {
    const min = canvasHeight * HUNT_EDGE_Y_MIN;
    const max = canvasHeight * HUNT_EDGE_Y_MAX - birdHeight;

    // Mostly uniform across the full band so birds aren't clustered in the middle.
    if (Math.random() < 0.75) {
        return min + Math.random() * (max - min);
    }

    const lane = HUNT_SPAWN_LANES[Math.floor(Math.random() * HUNT_SPAWN_LANES.length)];
    const jitter = (Math.random() - 0.5) * canvasHeight * 0.05;
    return Math.max(min, Math.min(max, canvasHeight * lane + jitter));
}

/** Nudge birds back only if they drift into an extreme edge strip. */
function nudgeBirdFromEdges(bird: HuntBird, canvasHeight: number): void {
    const min = canvasHeight * HUNT_EDGE_Y_MIN;
    const max = canvasHeight * HUNT_EDGE_Y_MAX - bird.height;
    if (bird.y < min) {
        bird.y = min;
        if (bird.vy < 0) {
            bird.vy *= -0.5;
        }
    } else if (bird.y > max) {
        bird.y = max;
        if (bird.vy > 0) {
            bird.vy *= -0.5;
        }
    }
}

export function spawnBird(
    snapshot: HuntGameSnapshot,
    canvasWidth: number,
    canvasHeight: number,
    elapsedMs: number
): HuntBird | null {
    if (canvasWidth < 40 || canvasHeight < 40) {
        return null;
    }

    const type = pickBirdType(snapshot, elapsedMs);
    const { width, height } = birdDimensions(type);
    const direction: 1 | -1 = Math.random() < 0.5 ? 1 : -1;
    const diff = getSpawnDifficulty(elapsedMs);
    const x = direction === 1 ? -width : canvasWidth + width;
    const y = spawnY(canvasHeight, height);
    const vx = direction * randomBetween(diff.speedMin, diff.speedMax);
    const vy = randomBetween(-0.45, 0.45);

    if (type === 'bonus') {
        snapshot.bonusSpawned = true;
    }

    return {
        id: nextBirdId++,
        type,
        x,
        y,
        vx,
        vy,
        width,
        height,
        direction,
        frame: 0,
        frameTimer: 0,
        state: 'fly',
        rotation: 0,
    };
}

function hitBird(snapshot: HuntGameSnapshot, bird: HuntBird, now: number): HuntHitResult {
    bird.state = 'fall';
    bird.hitAt = now;
    bird.vx *= 0.35;
    bird.vy = 1.5;
    snapshot.hits[bird.type] += 1;
    snapshot.comboStreak += 1;

    const basePoints = BIRD_POINTS[bird.type];
    const milestone = getComboMilestone(snapshot.comboStreak);
    const comboBonus = milestone?.bonus ?? 0;
    if (comboBonus > 0) {
        snapshot.comboBonus += comboBonus;
    }

    snapshot.score += basePoints + comboBonus;
    snapshot.flashUntil = now + 50;
    return {
        points: basePoints + comboBonus,
        comboBonus,
        comboStreak: snapshot.comboStreak,
        comboMilestone: milestone?.streak ?? null,
    };
}

function ellipseHit(px: number, py: number, bird: HuntBird, canvasWidth: number): boolean {
    const cx = bird.x + bird.width / 2;
    const cy = bird.y + bird.height / 2;
    const edgeMargin = canvasWidth * 0.1;
    const nearEdge = cx < edgeMargin || cx > canvasWidth - edgeMargin;
    const rx = bird.width * (nearEdge ? 0.42 : 0.35);
    const ry = bird.height * 0.35;
    const dx = (px - cx) / rx;
    const dy = (py - cy) / ry;
    return dx * dx + dy * dy <= 1;
}

export function handlePointerShot(
    snapshot: HuntGameSnapshot,
    x: number,
    y: number,
    now: number,
    canvasWidth: number
): HuntHitResult | null {
    snapshot.pointerX = x;
    snapshot.pointerY = y;

    for (let i = snapshot.birds.length - 1; i >= 0; i--) {
        const bird = snapshot.birds[i];
        if (bird.state !== 'fly') {
            continue;
        }
        if (ellipseHit(x, y, bird, canvasWidth)) {
            return hitBird(snapshot, bird, now);
        }
    }
    return null;
}

export function updateGame(
    snapshot: HuntGameSnapshot,
    deltaMs: number,
    now: number,
    canvasWidth: number,
    canvasHeight: number,
    spawnTimerMs: number,
    elapsedMs: number
): { snapshot: HuntGameSnapshot; spawnTimerMs: number; shouldSpawn: boolean } {
    if (snapshot.finished) {
        return { snapshot, spawnTimerMs, shouldSpawn: false };
    }

    snapshot.timeLeftMs = Math.max(0, snapshot.timeLeftMs - deltaMs);
    if (snapshot.timeLeftMs <= 0) {
        snapshot.finished = true;
        return { snapshot, spawnTimerMs, shouldSpawn: false };
    }

    const diff = getSpawnDifficulty(elapsedMs);
    let nextSpawnTimer = spawnTimerMs - deltaMs;
    let shouldSpawn = false;
    if (nextSpawnTimer <= 0) {
        shouldSpawn = true;
        nextSpawnTimer = randomBetween(diff.minMs, diff.maxMs);
    }

    snapshot.birds = snapshot.birds.filter((bird) => {
        if (bird.state === 'dead') {
            return false;
        }

        if (bird.state === 'fall') {
            bird.y += bird.vy;
            bird.vy += 0.08;
            bird.rotation += bird.direction * 0.08;
            if (bird.hitAt && now - bird.hitAt > 450) {
                bird.state = 'dead';
                return false;
            }
            return true;
        }

        bird.x += bird.vx;
        bird.y += bird.vy;
        bird.vy += Math.sin((bird.x + bird.id * 17) / 40) * 0.015;
        nudgeBirdFromEdges(bird, canvasHeight);
        bird.frameTimer += deltaMs;
        if (bird.frameTimer > 120) {
            bird.frame = (bird.frame + 1) % HUNT_ASSETS[bird.type].frameCount;
            bird.frameTimer = 0;
        }

        const offScreen = bird.direction === 1
            ? bird.x > canvasWidth + bird.width
            : bird.x < -bird.width;

        if (offScreen) {
            if (bird.type === 'bonus') {
                snapshot.bonusMissed += 1;
            }
            snapshot.comboStreak = 0;
            return false;
        }

        return true;
    });

    return { snapshot, spawnTimerMs: nextSpawnTimer, shouldSpawn };
}

export function buildGameResult(snapshot: HuntGameSnapshot): HuntGameResult {
    return {
        score: snapshot.score,
        hits: { ...snapshot.hits },
        comboBonus: snapshot.comboBonus,
        durationMs: HUNT_DURATION_MS,
        mode: HUNT_BACKEND_MODE,
        bonusMissed: snapshot.bonusMissed,
    };
}

export function getNextSpawnInterval(elapsedMs: number): number {
    const diff = getSpawnDifficulty(elapsedMs);
    return randomBetween(diff.minMs, diff.maxMs);
}
